import { AlertTriangle, Bell, FileText, Lock, Activity } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { getActiveAnnouncements, type AnnouncementNote } from "@/api/settings.api";
import { cn } from "@/lib/utils";

const TYPE_META: Record<
  AnnouncementNote["category"],
  { label: string; color: string; bg: string; border: string; icon: typeof Bell }
> = {
  billing: { label: "Billing", color: "text-warning", bg: "bg-warning/10", border: "border-warning/30", icon: FileText },
  alert: { label: "Safety Alert", color: "text-rose-700 dark:text-rose-400", bg: "bg-rose-50 dark:bg-rose-950/40", border: "border-rose-200 dark:border-rose-800", icon: AlertTriangle },
  clinical: { label: "Clinical", color: "text-chart-4", bg: "bg-chart-4/10", border: "border-indigo-200 dark:border-indigo-800", icon: Activity },
  admin: { label: "Admin", color: "text-foreground/80", bg: "bg-muted", border: "border-border", icon: Lock },
};

export function PatientAnnouncementBanner({ patientId }: { patientId: string }) {
  const { data: announcements = [] } = useQuery({
    queryKey: ["announcements", patientId, "active"],
    queryFn: () => getActiveAnnouncements(patientId),
    enabled: !!patientId,
    staleTime: 60_000,
  });

  if (announcements.length === 0) return null;

  return (
    <div className="shrink-0 border-b border-border bg-card/80 backdrop-blur-sm">
      <div className="px-10 py-4 space-y-3 max-w-[1600px] mx-auto">
        {announcements.map((ann) => {
          const meta = TYPE_META[ann.category] ?? TYPE_META.admin;
          const Icon = meta.icon;
          return (
            <div
              key={ann.id}
              className={cn(
                "flex items-start gap-4 rounded-2xl border-2 px-5 py-4",
                meta.bg,
                meta.border,
              )}
            >
              <div className={cn("mt-0.5 shrink-0", meta.color)}>
                <Icon className="h-5 w-5" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <Bell className="h-3.5 w-3.5 text-muted-foreground" />
                  <span className={cn("text-[10px] font-black uppercase tracking-widest", meta.color)}>
                    {meta.label}
                  </span>
                  <span className="text-[10px] font-bold text-muted-foreground">·</span>
                  <span className="text-sm font-black text-foreground">{ann.title}</span>
                </div>
                <p className="text-xs font-medium text-foreground/80 mt-1 leading-relaxed">{ann.body}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
